import { Modal, Form, Input, Select, Button, Row, Col, Spin } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/lib/form";
import { Team, AvailableMember, Member } from "../types";

const ADMIN_ROLE_OPTIONS = [
  { value: 2, label: "Admin" },
  { value: 1, label: "Member" },
];

interface TeamModalProps {
  isOpen: boolean;
  editingTeam: Team | null;
  form: FormInstance;
  currentUser: any;
  availableMembers: AvailableMember[];
  loadingMembers: boolean;
  teamConfig: { min_size: number; team_size: number; teamname_regex: string | null };
  taskId: string;
  onSave: () => void;
  onCancel: () => void;
  isTaskAdmin?: boolean;
}

export const TeamModal: React.FC<TeamModalProps> = ({
  isOpen,
  editingTeam,
  form,
  currentUser,
  availableMembers,
  loadingMembers,
  teamConfig,
  onSave,
  onCancel,
  isTaskAdmin,
}) => {
  return (
    <Modal
      title={editingTeam ? "Edit Team" : "New Team"}
      open={isOpen}
      onOk={onSave}
      onCancel={onCancel}
      width={700}
      destroyOnClose
    >
      <Form
        key={editingTeam ? editingTeam.id : "new"}
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="name"
          label="Team Name"
          rules={[
            { required: true, message: "Please input team name!" },
            ...(teamConfig.teamname_regex
              ? [{
                  pattern: new RegExp(teamConfig.teamname_regex),
                  message: `Team name must match the pattern: ${teamConfig.teamname_regex}`,
                }]
              : []),
          ]}
        >
          <Input placeholder="Enter team name" />
        </Form.Item>

        {loadingMembers ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <Spin tip="กำลังโหลดรายชื่อสมาชิก..." />
          </div>
        ) : (
          <Form.List name="members" initialValue={editingTeam?.members || []}>
            {(fields, { add, remove }) => (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <label style={{ fontWeight: 500 }}>Members</label>
                    <span style={{ marginLeft: 8, color: "#666", fontSize: 12 }}>
                      (Min: {teamConfig.min_size} / Max: {teamConfig.team_size} members)
                    </span>
                  </div>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    size="small"
                    disabled={availableMembers.length === 0 || (!isTaskAdmin && fields.length >= teamConfig.team_size)}
                  >
                    + Add Member
                  </Button>
                </div>

                {availableMembers.length === 0 && (
                  <div
                    style={{
                      padding: 16,
                      marginTop: 8,
                      background: "#f5f5f5",
                      borderRadius: 4,
                      textAlign: "center",
                      color: "#666",
                    }}
                  >
                    ไม่มีสมาชิกที่สามารถเพิ่มได้ในขณะนี้
                  </div>
                )}

                {fields.map(({ key, name, ...restField }) => {
                  const currentMember = form.getFieldValue("members")?.[name];
                  const isCurrentUser =
                    !editingTeam &&
                    currentUser &&
                    currentMember?.user_id === currentUser.user_id;

                  const isExistingMember =
                    editingTeam &&
                    editingTeam.members.some(
                      (m: Member) =>
                        m.id === (currentMember?.user_id || currentMember?.id)
                    );

                  return (
                    <Row
                      gutter={8}
                      key={key}
                      style={{ marginTop: 16 }}
                      align="middle"
                    >
                      {(!editingTeam && isCurrentUser) ||
                      (editingTeam && isExistingMember) ? (
                        <>
                          <Col span={16}>
                            <Input
                              disabled
                              value={
                                !editingTeam && isCurrentUser
                                  ? `${currentMember.name} (${currentMember.email}) - You`
                                  : `${currentMember.name} (${currentMember.email})`
                              }
                              style={{
                                backgroundColor: "#f0f0f0",
                                color: "#666",
                              }}
                            />
                          </Col>
                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, "role"]}
                              rules={[{ required: true, message: "Role" }]}
                              style={{ marginBottom: 0 }}
                            >
                              <Select
                                placeholder="Role"
                                style={{ width: "100%" }}
                              >
                                <Select.Option value={1}>Leader</Select.Option>
                                <Select.Option value={0}>Member</Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          {/* <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, "admin_role"]}
                              rules={[{ required: true, message: "Role" }]}
                              style={{ marginBottom: 0 }}
                            >
                              <Select
                                placeholder="Role"
                                style={{ width: "100%" }}
                                options={ADMIN_ROLE_OPTIONS}
                              />
                            </Form.Item>
                          </Col> */}
                        </>
                      ) : (
                        <>
                          <Col span={16}>
                            <Form.Item
                              {...restField}
                              name={[name, "user_id"]}
                              rules={[
                                {
                                  required: true,
                                  message: "กรุณาเลือกสมาชิก",
                                },
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Select
                                showSearch
                                placeholder="ค้นหาและเลือกสมาชิก"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  (option?.label ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                options={(() => {
                                  const options: any[] = [];

                                  if (editingTeam) {
                                    availableMembers.forEach((member: AvailableMember) => {
                                      options.push({
                                        value: member.user_id,
                                        label: `${member.name} (${member.email})`,
                                        member: member,
                                      });
                                    });
                                  } else {
                                    availableMembers.forEach((member: AvailableMember) => {
                                      if (
                                        currentUser &&
                                        member.user_id !== currentUser.user_id
                                      ) {
                                        options.push({
                                          value: member.user_id,
                                          label: `${member.name} (${member.email})`,
                                          member: member,
                                        });
                                      }
                                    });
                                  }

                                  return options;
                                })()}
                                onChange={(_value, option: any) => {
                                  const selectedMember = option.member;
                                  if (selectedMember) {
                                    const members =
                                      form.getFieldValue("members");
                                    members[name] = {
                                      ...members[name],
                                      user_id: selectedMember.user_id,
                                      name: selectedMember.name,
                                      email: selectedMember.email,
                                      id: selectedMember.user_id,
                                    };
                                    form.setFieldsValue({ members });
                                  }
                                }}
                              />
                            </Form.Item>
                          </Col>

                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, "role"]}
                              rules={[{ required: true, message: "Role" }]}
                              initialValue={0}
                              style={{ marginBottom: 0 }}
                            >
                              <Select
                                placeholder="Role"
                                style={{ width: "100%" }}
                              >
                                <Select.Option value={1}>Leader</Select.Option>
                                <Select.Option value={0}>Member</Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                        </>
                      )}

                      <Col span={2} style={{ textAlign: "center" }}>
                        {!isCurrentUser && (
                          <Button
                            size="small"
                            icon={
                              <DeleteOutlined style={{ color: "#B31312" }} />
                            }
                            style={{ backgroundColor: "#FFDFDF" }}
                            onClick={() => remove(name)}
                          />
                        )}
                      </Col>
                    </Row>
                  );
                })}
              </>
            )}
          </Form.List>
        )}
      </Form>
    </Modal>
  );
};
